import { useEffect, useRef, useState } from "react";
import { AutoComplete, AutoCompleteChangeEvent, AutoCompleteCompleteEvent } from "primereact/autocomplete";

type Option = Record<string, any>;

interface Props {
  value: string | number;
  options?: Option[];
  onChange: (value: string | number) => void;
  optionLabel?: string;
  optionValue?: string;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

// A stable empty array: a fresh `[]` on every render would restart the sync effect below.
const NO_OPTIONS: Option[] = [];

const label = (option: Option | null, field: string) => (option ? String(option[field] ?? '') : '');

/**
 * A dropdown that is searched in its own input box instead of in a second
 * filter box inside the panel: click (or tab into) the field and type to
 * narrow the list, or use the chevron to browse all of it.
 *
 * The props mirror the subset of PrimeReact's Dropdown the forms use, so it
 * drops straight into their place.
 */
export default function SearchSelect({
  value,
  options = NO_OPTIONS,
  onChange,
  optionLabel = 'name',
  optionValue = 'id',
  disabled,
  className,
  placeholder,
}: Props) {
  const autoComplete = useRef<AutoComplete>(null);
  const selected = options.find((option) => option[optionValue] === value) ?? null;

  // What the input shows: the selected option, or the text being typed over it.
  const [typed, setTyped] = useState<Option | string | null>(selected);
  const [suggestions, setSuggestions] = useState<Option[]>(options);
  // Whether the arrow keys have moved the highlight since the text last changed.
  const navigated = useRef(false);

  // Follow the selection when it changes outside the box: a form reset, a
  // loaded document, or a parent dropdown clearing this one. Text being typed
  // survives, so a dictionary arriving mid-word cannot wipe the input; blur
  // resolves it against the options that have just landed.
  useEffect(() => {
    setTyped((current) =>
      typeof current === 'string' ? current : options.find((option) => option[optionValue] === value) ?? null,
    );
  }, [value, options, optionValue]);

  const matches = (text: string) => {
    const query = text.trim().toLowerCase();
    return query ? options.filter((option) => label(option, optionLabel).toLowerCase().includes(query)) : options.slice();
  };

  // The option this text names outright, as opposed to merely starting.
  const exact = (text: string) => {
    const name = text.trim().toLowerCase();
    return name ? options.find((option) => label(option, optionLabel).trim().toLowerCase() === name) ?? null : null;
  };

  // The panel is up, or on its way up, whenever its overlay is in the DOM.
  // PrimeReact reports `onShow` only after a 120 ms transition, so going by
  // that would re-run the search for a click or a second ArrowDown inside the
  // window, replacing the typed filter with the full list.
  const browseAll = (event: React.SyntheticEvent) => {
    if (!autoComplete.current?.getOverlay()) {
      autoComplete.current?.search(event, '', 'dropdown');
    }
  };

  const handleChange = (event: AutoCompleteChangeEvent) => {
    if (event.value && typeof event.value === 'object') {
      // A pick. Enter and Tab take whatever row is highlighted, and
      // `autoHighlight` leaves that on the first match -- which is the wrong
      // option when the text names another one outright, as "Kutaisi" does
      // with "Kutaisi district" listed above it. A row the arrows or the mouse
      // chose is the user's own, so it is taken as it is.
      const byHand = navigated.current || event.originalEvent?.type !== 'keydown';
      const picked = (byHand ? null : exact(typeof typed === 'string' ? typed : '')) ?? event.value;
      setTyped(picked);
      onChange(picked[optionValue]);
      return;
    }
    // Typing only filters; the selection itself changes on pick or on blur, so
    // that half-typed text never resets the dropdowns that depend on this one.
    navigated.current = false;
    setTyped(event.value);
  };

  const handleKeyUp = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      navigated.current = true;
    }
    if (event.key === 'ArrowDown') {
      browseAll(event);
    }
  };

  // Leaving the field has to resolve whatever is in it: an exact name (or the
  // single remaining match) selects, an empty box clears, anything else goes
  // back to the current selection rather than silently dropping it.
  const handleBlur = () => {
    if (typeof typed !== 'string') {
      return;
    }
    const text = typed.trim();
    if (!text) {
      setTyped(null);
      if (value !== '') {
        onChange('');
      }
      return;
    }
    const found = matches(text);
    const resolved = exact(text) ?? (found.length === 1 ? found[0] : null);
    if (resolved) {
      setTyped(resolved);
      if (resolved[optionValue] !== value) {
        onChange(resolved[optionValue]);
      }
    } else {
      setTyped(selected);
    }
  };

  return (
    <AutoComplete
      ref={autoComplete}
      value={typed}
      suggestions={suggestions}
      completeMethod={(event: AutoCompleteCompleteEvent) => setSuggestions(matches(event.query))}
      field={optionLabel}
      dropdown
      dropdownMode="blank"
      autoHighlight
      disabled={disabled}
      placeholder={placeholder}
      className={className}
      inputClassName={className}
      onChange={handleChange}
      onFocus={(e) => e.target.select()}
      onClick={browseAll}
      onKeyUp={handleKeyUp}
      onBlur={handleBlur}
    />
  );
}
